import { useEffect, useMemo, useRef, useState } from 'react'
import {
  startOfMonth,
  startOfWeek,
  endOfMonth,
  endOfWeek,
  addMonths,
  addDays,
  isSameDay,
  isSameMonth,
  isBefore,
  getDate,
  format,
} from 'date-fns'

type MobileMonthScrollerProps = {
  currentDate: Date
  eventMap: Map<string, { title: string }[]>
  selectedDate: Date | null
  onSelectDay: (date: Date) => void
  onChangeCurrentDate: (date: Date) => void
}

const MONTH_PRELOAD = 6

const buildWeeks = (date: Date): Date[][] => {
  const start = startOfWeek(startOfMonth(date))
  const end = endOfWeek(endOfMonth(date))
  const weeks: Date[][] = []
  let cursor = start
  while (isBefore(cursor, end) || isSameDay(cursor, end)) {
    const week: Date[] = []
    for (let i = 0; i < 7; i += 1) {
      week.push(cursor)
      cursor = addDays(cursor, 1)
    }
    weeks.push(week)
  }
  return weeks
}

function MobileMonthScroller({
  currentDate,
  eventMap,
  selectedDate,
  onSelectDay,
  onChangeCurrentDate,
}: MobileMonthScrollerProps) {
  const [offsets, setOffsets] = useState(() =>
    Array.from({ length: MONTH_PRELOAD * 2 + 1 }, (_, i) => i - MONTH_PRELOAD),
  )
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const hasPositionedRef = useRef(false)

  const months = useMemo(
    () =>
      offsets.map((offset) => {
        const base = startOfMonth(currentDate)
        return addMonths(base, offset)
      }),
    [currentDate, offsets],
  )

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    if (hasPositionedRef.current) return
    const target = scroller.querySelector<HTMLElement>('[data-offset="0"]')
    if (target) {
      scroller.scrollTop = target.offsetTop - scroller.clientHeight * 0.25
      hasPositionedRef.current = true
    }
  }, [months])

  useEffect(() => {
    setOffsets(Array.from({ length: MONTH_PRELOAD * 2 + 1 }, (_, i) => i - MONTH_PRELOAD))
    hasPositionedRef.current = false
  }, [currentDate])

  const extendMonths = (direction: 'up' | 'down') => {
    setOffsets((prev) => {
      const min = Math.min(...prev)
      const max = Math.max(...prev)
      if (direction === 'up') {
        const extra = Array.from({ length: MONTH_PRELOAD }, (_, i) => min - (MONTH_PRELOAD - i))
        return [...extra, ...prev]
      }
      const extra = Array.from({ length: MONTH_PRELOAD }, (_, i) => max + i + 1)
      return [...prev, ...extra]
    })
  }

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const handle = () => {
      const buffer = 320
      const prevHeight = scroller.scrollHeight
      if (scroller.scrollTop < buffer) {
        extendMonths('up')
        requestAnimationFrame(() => {
          const nextHeight = scroller.scrollHeight
          scroller.scrollTop += nextHeight - prevHeight
        })
      } else if (scroller.scrollTop + scroller.clientHeight > scroller.scrollHeight - buffer) {
        extendMonths('down')
      }
    }
    scroller.addEventListener('scroll', handle, { passive: true })
    return () => scroller.removeEventListener('scroll', handle)
  }, [])

  const handleSelectDay = (date: Date) => {
    onSelectDay(date)
    onChangeCurrentDate(date)
  }

  return (
    <div className="mobile-month-scroller" ref={scrollerRef}>
      {months.map((monthDate, idx) => {
        const offset = offsets[idx]
        const weeks = buildWeeks(monthDate)
        const monthKey = format(monthDate, 'yyyy-MM')
        return (
          <section key={`${monthKey}-${offset}`} className="mobile-month" data-offset={offset}>
            <header className="mobile-month-header">
              <span className="mobile-month-name">{format(monthDate, 'MMMM')}</span>
              <span className="mobile-month-year">{format(monthDate, 'yyyy')}</span>
            </header>
            <div className="mobile-week-labels">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            {weeks.map((week, weekIdx) => (
              <div key={`${monthKey}-week-${weekIdx}`} className="mobile-week-row">
                {week.map((day) => {
                  const dayKey = format(day, 'yyyy-MM-dd')
                  const dayEvents = eventMap.get(dayKey) ?? []
                  const isCurrentMonth = isSameMonth(day, monthDate)
                  const isToday = isSameDay(day, new Date())
                  const isSelected = selectedDate ? isSameDay(day, selectedDate) : false
                  return (
                    <button
                      type="button"
                      key={dayKey}
                      className={`mobile-day ${isCurrentMonth ? '' : 'muted'} ${isToday ? 'today' : ''} ${
                        dayEvents.length ? 'has-events' : ''
                      } ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectDay(day)}
                    >
                      <span className="mobile-day-number">{getDate(day)}</span>
                      {dayEvents.length > 0 && (
                        <div className="mobile-event-dots" aria-label={`${dayEvents.length} events`}>
                          {dayEvents.slice(0, 3).map((evt, i) => (
                            <span className="mobile-dot" key={`${dayKey}-dot-${i}`} title={evt.title} />
                          ))}
                          {dayEvents.length > 3 && <span className="mobile-more">+{dayEvents.length - 3}</span>}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
          </section>
        )
      })}
    </div>
  )
}

export default MobileMonthScroller
